import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { clientesService, Cliente, CreateClienteDto, UpdateClienteDto } from '../src/services/clientesService';
import { empresasService, Empresa } from '../src/services/empresasService';
import { useAuth } from '../src/contexts/AuthContext';

interface ClientesProps {
  onClose: () => void;
}

interface ClienteFormData {
  empresaId: number;
  clienteId: string;
  nombre: string;
  apellido: string;
  telefono: string;
  email: string;
  direccion: string;
  fechaNacimiento: string;
  genero: string;
  activo: boolean;
  notas: string;
}

type SortableKey = 'nombre' | 'apellido' | 'telefono' | 'activo' | 'fechaCreacion';

const emptyFormData: ClienteFormData = {
  empresaId: 0,
  clienteId: '',
  nombre: '',
  apellido: '',
  telefono: '',
  email: '',
  direccion: '',
  fechaNacimiento: '',
  genero: '',
  activo: true,
  notas: '',
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: '2-digit' });
};

const Clientes: React.FC<ClientesProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';

  // Inject CSS styles for modal animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(0.001); opacity: 0; }
      }
      @keyframes crecer {
        0% { transform: scale(0.001); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Default to user's empresa if not SuperAdmin
  const [empresaFilterId, setEmpresaFilterId] = useState<number | 'all'>(user?.empresaId ?? 'all');
  const [sortConfig, setSortConfig] = useState<{ key: SortableKey; direction: 'asc' | 'desc' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [formData, setFormData] = useState<ClienteFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Update empresaFilterId when user becomes available
  useEffect(() => {
    if (user?.empresaId && !isSuperAdmin) {
      setEmpresaFilterId(user.empresaId);
    }
  }, [user?.empresaId, isSuperAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientesData, empresasData] = await Promise.all([
        clientesService.getAll(),
        empresasService.getAll()
      ]);
      setClientes(clientesData);
      setEmpresas(empresasData.filter((e: Empresa) => e.activa));
    } catch (err) {
      showErrorAlert(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const empresaById = useMemo(() => {
    return empresas.reduce<Record<number, Empresa>>((acc, empresa) => {
      acc[empresa.empresaId] = empresa;
      return acc;
    }, {});
  }, [empresas]);

  const filteredClientes = useMemo(() => {
    const loweredTerm = searchTerm.toLowerCase();
    let filtered = clientes.filter((cliente) =>
      cliente.nombre.toLowerCase().includes(loweredTerm) ||
      cliente.apellido.toLowerCase().includes(loweredTerm) ||
      cliente.telefono.toLowerCase().includes(loweredTerm) ||
      cliente.clienteId.toLowerCase().includes(loweredTerm) ||
      (cliente.email && cliente.email.toLowerCase().includes(loweredTerm))
    );
    if (empresaFilterId !== 'all') {
      filtered = filtered.filter((cliente) => cliente.empresaId === empresaFilterId);
    }
    if (sortConfig !== null) {
      filtered = [...filtered].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        if (sortConfig.key === 'activo') {
          return (a.activo === b.activo ? 0 : a.activo ? -1 : 1) * direction;
        }
        const aValue = (a as any)[sortConfig.key] ?? '';
        const bValue = (b as any)[sortConfig.key] ?? '';
        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        return 0;
      });
    }
    return filtered;
  }, [clientes, searchTerm, sortConfig, empresaFilterId]);

  const showErrorAlert = (message: string) => {
    Swal.fire({
      title: 'Error',
      text: message,
      icon: 'error',
      background: 'rgba(15,23,42,0.85)',
      color: '#fff',
      confirmButtonColor: '#ef4444',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
      });
  };

  const handleSort = (key: SortableKey) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const openCreateModal = () => {
    setEditingCliente(null);
    // Default to empresaFilterId, or user's empresaId for non-SuperAdmin
    const defaultEmpresaId = isSuperAdmin 
      ? (empresaFilterId !== 'all' ? empresaFilterId : (empresas.find(e => e.activa)?.empresaId ?? 0))
      : (user?.empresaId ?? 0);
    setFormData({ ...emptyFormData, empresaId: defaultEmpresaId });
    setIsModalOpen(true);
  };

  const openEditModal = (cliente: Cliente) => {
    setEditingCliente(cliente);
    setFormData({
      empresaId: cliente.empresaId,
      clienteId: cliente.clienteId,
      nombre: cliente.nombre,
      apellido: cliente.apellido,
      telefono: cliente.telefono,
      email: cliente.email || '',
      direccion: cliente.direccion || '',
      fechaNacimiento: cliente.fechaNacimiento ? cliente.fechaNacimiento.split('T')[0] : '',
      genero: cliente.genero || '',
      activo: cliente.activo,
      notas: cliente.notas || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setEditingCliente(null);
      setFormData(emptyFormData);
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.empresaId === 0) {
      showErrorAlert('Debe seleccionar una empresa');
      return;
    }
    setSaving(true);
    try {
      const dtoData: CreateClienteDto = {
        empresaId: formData.empresaId,
        clienteId: formData.clienteId,
        nombre: formData.nombre,
        apellido: formData.apellido,
        telefono: formData.telefono,
        email: formData.email || null,
        direccion: formData.direccion || null,
        fechaNacimiento: formData.fechaNacimiento || null,
        genero: formData.genero || null,
        activo: formData.activo,
        notas: formData.notas || null,
      };
      if (editingCliente) {
        const updateData: UpdateClienteDto = { ...dtoData };
        const updated = await clientesService.update(editingCliente.id, updateData);
        setClientes(clientes.map(c => c.id === updated.id ? updated : c));
        Swal.fire({ title: 'Cliente actualizado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false });
      } else {
        const created = await clientesService.create(dtoData);
        setClientes([created, ...clientes]);
        Swal.fire({ title: 'Cliente creado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false });
      }
      closeModal();
    } catch (err) {
      showErrorAlert(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (cliente: Cliente) => {
    Swal.fire({
      title: '¿Eliminar cliente?',
      html: `Esta acción eliminará al cliente <strong>${cliente.nombre} ${cliente.apellido}</strong>.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await clientesService.delete(cliente.id);
        setClientes(clientes.filter(c => c.id !== cliente.id));
        Swal.fire({ title: 'Cliente eliminado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false });
      } catch (err) {
        showErrorAlert(err instanceof Error ? err.message : 'Error al eliminar');
      }
    });
  };

  const handleToggleStatus = (cliente: Cliente) => {
    const action = cliente.activo ? 'desactivar' : 'activar';
    Swal.fire({
      title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} cliente?`,
      html: `El cliente <strong>${cliente.nombre} ${cliente.apellido}</strong> será ${cliente.activo ? 'desactivado' : 'reactivado'}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: `Sí, ${action}`,
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const updateData: UpdateClienteDto = {
          empresaId: cliente.empresaId, clienteId: cliente.clienteId, nombre: cliente.nombre, apellido: cliente.apellido,
          telefono: cliente.telefono, email: cliente.email, direccion: cliente.direccion, fechaNacimiento: cliente.fechaNacimiento,
          genero: cliente.genero, activo: !cliente.activo, notas: cliente.notas,
        };
        const updated = await clientesService.update(cliente.id, updateData);
        setClientes(clientes.map(c => c.id === updated.id ? updated : c));
        Swal.fire({ title: `Cliente ${!cliente.activo ? 'activado' : 'desactivado'}`, icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 1500, timerProgressBar: true, backdrop: false });
      } catch (err) {
        showErrorAlert(err instanceof Error ? err.message : 'Error al actualizar estado');
      }
    });
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      {/* Header */}
      <div className="bg-white dark:bg-background-dark text-gray-900 dark:text-white px-4 sm:px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-900 dark:text-white/80 hover:text-gray-900 dark:hover:text-white transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
            <span className="hidden sm:inline">Volver</span>
          </button>
          <div>
            <h1 className="text-2xl text-secondary dark:text-white">Clientes</h1>
            <p className="text-gray/70 text-sm hidden sm:block">Administra los clientes registrados en la plataforma</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-[#307CAF] dark:bg-slate-800 text-white px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
              <input type="text" placeholder="Buscar por nombre, apellido, teléfono o email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent" />
            </div>
          </div>
          {isSuperAdmin && (
            <div className="sm:w-64">
              <select value={empresaFilterId} onChange={(e) => setEmpresaFilterId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="w-full px-3 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent">
                <option value="all">Todas las empresas</option>
                {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <span>{filteredClientes.length} clientes</span>
            <button onClick={() => loadData()} className="inline-flex items-center gap-1 px-3 py-1 border border-white/20 dark:border-slate-600 rounded-full hover:bg-white/10 dark:hover:bg-slate-700 transition" title="Recargar">
              <span className="material-symbols-outlined text-base">refresh</span>Recargar
            </button>
            <button onClick={openCreateModal} className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#307CAF] dark:bg-slate-600 dark:text-white rounded-full font-semibold hover:bg-white/90 dark:hover:bg-slate-500 transition">
              <span className="material-symbols-outlined text-base">add</span>Nuevo Cliente
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <div className="animate-spin w-8 h-8 border-4 border-[#307CAF] border-t-transparent rounded-full mx-auto mb-4"></div>Cargando clientes...
            </div>
          ) : filteredClientes.length === 0 ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4 block">badge</span>No se encontraron clientes con ese criterio.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-[#307CAF] text-white dark:bg-slate-700">
                  <tr>
                    <th onClick={() => handleSort('nombre')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Cliente{sortConfig?.key === 'nombre' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th onClick={() => handleSort('telefono')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Teléfono{sortConfig?.key === 'telefono' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email</th>
                    <th onClick={() => handleSort('activo')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Estado{sortConfig?.key === 'activo' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th onClick={() => handleSort('fechaCreacion')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Registro{sortConfig?.key === 'fechaCreacion' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredClientes.map((cliente) => (
                    <tr key={cliente.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#307CAF]/10 flex items-center justify-center">
                            <span className="text-[#307CAF] font-bold text-sm">{cliente.nombre.charAt(0)}{cliente.apellido.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">{cliente.nombre} {cliente.apellido}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">ID: {cliente.clienteId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{cliente.telefono}</td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{empresaById[cliente.empresaId]?.nombreEmpresa || cliente.nombreEmpresa || `ID: ${cliente.empresaId}`}</td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{cliente.email || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${cliente.activo ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                          <span className="material-symbols-outlined text-sm">{cliente.activo ? 'verified' : 'visibility_off'}</span>{cliente.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{formatDate((cliente as any).fechaCreacion)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEditModal(cliente)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">
                            <span className="material-symbols-outlined text-sm">edit</span>Editar
                          </button>
                          <button onClick={() => handleToggleStatus(cliente)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-[#307CAF] hover:bg-[#307CAF]/10">
                            <span className="material-symbols-outlined text-sm">{cliente.activo ? 'toggle_off' : 'toggle_on'}</span>{cliente.activo ? 'Desactivar' : 'Activar'}
                          </button>
                          <button onClick={() => handleDelete(cliente)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                            <span className="material-symbols-outlined text-sm">delete</span>Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="relative w-full max-w-lg rounded-2xl bg-white/95 dark:bg-slate-800 p-6 shadow-xl backdrop-blur-sm border border-gray-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto" style={{ animation: isModalClosing ? 'disminuir 0.3s ease-out' : 'crecer 0.3s ease-out' }}>
            <button onClick={closeModal} className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
              <span className="material-symbols-outlined">close</span>
            </button>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{editingCliente ? 'Editar cliente' : 'Registrar cliente'}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{editingCliente ? 'Modifica los datos del cliente.' : 'Añade los datos del nuevo cliente.'}</p>
            <form className="space-y-4" onSubmit={handleSubmit}>
              {!editingCliente && (
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">ID Cliente (Teléfono 10 dígitos) *</label>
                  <input type="text" required value={formData.clienteId} onChange={(e) => setFormData(prev => ({ ...prev, clienteId: e.target.value.replace(/\D/g, '').slice(0, 10) }))} maxLength={10}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" placeholder="Ej. 5512345678" />
                  <p className="text-xs text-slate-500 mt-1">Este será el identificador único del cliente</p>
                </div>
              )}
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Empresa *</label>
                  <select required value={formData.empresaId} onChange={(e) => setFormData(prev => ({ ...prev, empresaId: Number(e.target.value) }))}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]">
                    <option value={0}>Seleccionar empresa...</option>
                    {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Nombre *</label>
                  <input type="text" required value={formData.nombre} onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))} maxLength={100}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" placeholder="Ej. Juan" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Apellido *</label>
                  <input type="text" required value={formData.apellido} onChange={(e) => setFormData(prev => ({ ...prev, apellido: e.target.value }))} maxLength={100}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" placeholder="Ej. Pérez" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Teléfono de contacto *</label>
                <input type="tel" required value={formData.telefono} onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))} maxLength={20}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" placeholder="Ej. +52 55 1234 5678" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} maxLength={100}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" placeholder="Ej. juan@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Dirección</label>
                <input type="text" value={formData.direccion} onChange={(e) => setFormData(prev => ({ ...prev, direccion: e.target.value }))} maxLength={200}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" placeholder="Ej. Calle 123, Col. Centro" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Fecha de nacimiento</label>
                  <input type="date" value={formData.fechaNacimiento} onChange={(e) => setFormData(prev => ({ ...prev, fechaNacimiento: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Género</label>
                  <select value={formData.genero} onChange={(e) => setFormData(prev => ({ ...prev, genero: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]">
                    <option value="">Seleccionar</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Notas</label>
                <textarea value={formData.notas} onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))} maxLength={500} rows={3}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF] resize-none" placeholder="Observaciones adicionales..." />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-slate-600 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Estado</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Define si el cliente está activo en el sistema.</p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <span className="mr-3 text-sm text-slate-500 dark:text-slate-400">{formData.activo ? 'Activo' : 'Inactivo'}</span>
                  <input type="checkbox" className="sr-only" checked={formData.activo} onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked }))} />
                  <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.activo ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                    <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.activo ? 'translate-x-5' : ''}`} />
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" className="rounded-lg border border-gray-200 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={closeModal}>Cancelar</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-[#307CAF]/90 disabled:opacity-60">
                  {saving ? (<><span className="material-symbols-outlined animate-spin">progress_activity</span>Guardando...</>) : (<><span className="material-symbols-outlined">save</span>Guardar</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;
